import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  HomeIcon,
  MegaphoneIcon,
  PhoneIcon,
  PhoneArrowUpRightIcon,
  CogIcon,
  UserGroupIcon,
  ServerIcon,
  ClockIcon,
  ChartBarIcon,
  MagnifyingGlassCircleIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  BoltIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useOptionalWebRTC } from '../../contexts/WebRTCContext';
import { clsx } from 'clsx';
import Button from '../UI/Button';
import { agentService } from '../../services/agentService';
import { phoneNumberHealthService, type PhoneHealthSummary } from '../../services/phoneNumberHealthService';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Campaigns', href: '/campaigns', icon: MegaphoneIcon },
  { name: 'SMS Inbox', href: '/sms/inbox', icon: ChatBubbleLeftRightIcon },
  { name: 'View Results', href: '/results', icon: ChartBarIcon },
  { name: 'Duplicate Leads', href: '/duplicate-leads', icon: MagnifyingGlassCircleIcon },
  { name: 'Call History', href: '/calls', icon: ClockIcon },
];

const adminMainNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Campaigns', href: '/campaigns', icon: MegaphoneIcon },
  { name: 'SMS Inbox', href: '/sms/inbox', icon: ChatBubbleLeftRightIcon },
  { name: 'View Charges', href: '/charges', icon: CurrencyDollarIcon },
  { name: 'View Results', href: '/results', icon: ChartBarIcon },
  { name: 'Duplicate Leads', href: '/duplicate-leads', icon: MagnifyingGlassCircleIcon },
  { name: 'Call History', href: '/calls', icon: ClockIcon },
];

const agentNavigation = [
  { name: 'Agent Dashboard', href: '/agent-dashboard', icon: PhoneIcon },
  { name: 'SMS Inbox', href: '/sms/inbox', icon: ChatBubbleLeftRightIcon },
  { name: 'Manual Dialer', href: '/agent-manual-dial', icon: PhoneArrowUpRightIcon },
  { name: 'Bot Monitor', href: '/admin/bot-monitor', icon: CpuChipIcon },
  { name: 'Call History', href: '/agent-calls', icon: ClockIcon },
  { name: 'View Results', href: '/agent-results', icon: ChartBarIcon },
];

const adminNavigation = [
  { name: 'Live Monitor', href: '/live-monitor', icon: PhoneArrowUpRightIcon },
  { name: 'Auto Dial Monitor', href: '/auto-dial-monitor', icon: BoltIcon },
  { name: 'Connect Report', href: '/connect-report', icon: ClockIcon },
  { name: 'SMS Live Monitor', href: '/sms-live-monitor', icon: ChatBubbleLeftRightIcon },
  { name: 'Bot Monitor', href: '/admin/bot-monitor', icon: CpuChipIcon },
  { name: 'Pipeline', href: '/admin/pipeline', icon: ChartBarIcon },
  { name: 'Sub-Stages', href: '/admin/sub-stages', icon: DocumentTextIcon },
  { name: 'SMS Templates', href: '/admin/sms-templates', icon: ChatBubbleLeftRightIcon },
  { name: 'Admin', href: '/admin', icon: UserGroupIcon },
  { name: 'Area Codes', href: '/admin/area-codes', icon: MapPinIcon },
  { name: 'Scripts', href: '/scripts', icon: DocumentTextIcon },
  { name: 'Phone Numbers', href: '/phone-numbers', icon: PhoneIcon },
  { name: 'Webhooks', href: '/webhooks', icon: ServerIcon },
  { name: 'Settings', href: '/settings', icon: CogIcon },
];

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const webrtc = useOptionalWebRTC();

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const isAgent = user?.role === 'agent';
  const mainNavigation = isAgent ? agentNavigation : (isAdmin ? adminMainNavigation : navigation);

  // Agent availability state
  const [isAvailable, setIsAvailable] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  /** Heartbeats are failing — the dialer is about to stop seeing this agent. */
  const [presenceLost, setPresenceLost] = useState(false);
  const [phoneHealth, setPhoneHealth] = useState<PhoneHealthSummary | null>(null);

  useEffect(() => {
    if (isAdmin) {
      phoneNumberHealthService.getSummary().then(setPhoneHealth).catch(() => {});
    }
  }, [isAdmin]);

  // Fetch initial availability for agent users
  useEffect(() => {
    if (isAgent) {
      agentService.getAvailability().then(setIsAvailable).catch(() => {});
    }
  }, [isAgent]);

  // Heartbeat: send presence ping every 30 seconds while agent is available.
  //
  // The ping is what keeps users.last_seen_at fresh, and the dialer drops an
  // agent from the pool after AGENT_ONLINE_HEARTBEAT_MS (2 min) without one.
  // Swallowing the failure silently — as this used to — left the toggle reading
  // "Available" while the backend had already stopped routing calls, which is
  // indistinguishable from a quiet dialer.
  const heartbeatFailures = useRef(0);

  /**
   * Read through a ref, never a dependency.
   *
   * The WebRTC context value is rebuilt on every provider render, and the call
   * timer re-renders it once a second while an agent is on a call. Depending on
   * the object here rebuilt this callback each second, which tore down and
   * re-armed the interval effect below — and that effect pings immediately on
   * arming. The result was a heartbeat every second per in-call agent instead of
   * every thirty.
   */
  const webrtcRef = useRef(webrtc);
  webrtcRef.current = webrtc;

  const sendPresencePing = useCallback(async () => {
    const rtc = webrtcRef.current;
    try {
      // The ping doubles as the parked-leg reconciliation point: the browser
      // reports the leg it holds, the server answers with what to do about it.
      const result = await agentService.sendHeartbeat(
        rtc?.getParkCallId?.() ?? null,
        rtc?.status ?? null
      );
      rtc?.applyParkDirective?.(result);
      heartbeatFailures.current = 0;
      setPresenceLost(false);
    } catch {
      heartbeatFailures.current += 1;
      // Two misses is 60s at this interval: long enough not to cry wolf over one
      // flaky request, and still a minute before the dialer stops routing.
      if (heartbeatFailures.current >= 2) setPresenceLost(true);
    }
  }, []);

  useEffect(() => {
    if (!isAgent) return;
    void sendPresencePing();
    const interval = setInterval(() => {
      void sendPresencePing();
    }, 30000);
    return () => clearInterval(interval);
  }, [isAgent, sendPresencePing]);

  /**
   * Report a change of call state at once, rather than up to 30s later.
   *
   * The routing side now treats a client that says `in-call` as occupied, which
   * makes the timeliness of this value part of how many calls an agent gets: on
   * the interval alone, an agent stays "busy" for the rest of the tick after
   * hanging up, and is passed over the whole time. The availability toggle
   * already pings for the same reason a few lines below.
   *
   * Keyed on the status string, never the context object. The value changes
   * twice a call; the object is rebuilt every second by the call timer, and
   * depending on it here is what once turned this into a heartbeat per second —
   * see the note on `webrtcRef` above.
   */
  const rtcStatus = webrtc?.status ?? null;
  useEffect(() => {
    if (!isAgent || !rtcStatus) return;
    void sendPresencePing();
  }, [isAgent, rtcStatus, sendPresencePing]);

  const handleAvailabilityToggle = useCallback(async () => {
    if (availabilityLoading) return;
    // Unlock audio playback inside this click gesture so the browser allows
    // the remote-audio element to play() when a routed call auto-answers.
    webrtc?.unlockAudio();
    setAvailabilityLoading(true);
    try {
      const newValue = !isAvailable;
      const result = await agentService.setAvailability(newValue, webrtc?.getParkCallId?.() ?? null);
      setIsAvailable(result?.isAvailable ?? newValue);
      if (result) webrtc?.applyParkDirective?.(result);
      // Going available mid-interval would otherwise wait up to 30s for the next
      // ping; the toggle itself now carries the park directive, so this is a
      // backup for presence only.
      if (result?.isAvailable) void sendPresencePing();
    } catch {
      // revert on error
    } finally {
      setAvailabilityLoading(false);
    }
  }, [isAvailable, availabilityLoading, webrtc, sendPresencePing]);

  const SidebarContent = () => (
    <div className="flex h-full flex-col bg-background">
      {/* Logo */}
      <div className="flex h-16 shrink-0 items-center px-6 border-b border-border/50">
        <Button to="/dashboard" variant="ghost" className="flex items-center group transition-all duration-200 hover-scale p-0 h-auto">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
            <PhoneIcon className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="ml-3 text-lg font-bold text-foreground group-hover:text-primary transition-colors duration-200">
            AI Calling Agent
          </span>
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col px-4 py-6">
        <ul role="list" className="flex flex-1 flex-col gap-y-8">
          <li>
            <div className="text-xs font-semibold leading-6 text-muted-foreground uppercase tracking-wider mb-3 px-2">
              Main
            </div>
            <ul role="list" className="space-y-1">
              {mainNavigation.map((item) => {
                const isActive = location.pathname === item.href ||
                  (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
                
                return (
                  <li key={item.name}>
                    <Button
                      to={item.href}
                      className={clsx(
                        isActive
                          ? 'bg-primary/10 text-primary dark:text-white border-r-2 border-primary'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                        'group flex gap-x-3 rounded-lg p-3 text-sm font-medium transition-all duration-200 hover-lift w-full justify-start'
                      )}
                      onClick={onClose}
                      variant="ghost"
                    >
                      <item.icon
                        className={clsx(
                          isActive ? 'text-primary dark:text-white' : 'text-muted-foreground group-hover:text-foreground',
                          'h-5 w-5 shrink-0'
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </li>

          {/* Admin Navigation */}
          {isAdmin && (
            <li>
              <div className="text-xs font-semibold leading-6 text-muted-foreground uppercase tracking-wider mb-3 px-2">
                Administration
              </div>
              <ul role="list" className="space-y-1">
                {adminNavigation.map((item) => {
                  const isActive = item.href === '/admin'
                    ? location.pathname === '/admin'
                    : location.pathname === item.href || location.pathname.startsWith(item.href);
                  
                  return (
                    <li key={item.name}>
                      <Button
                        to={item.href}
                        className={clsx(
                          isActive
                            ? 'bg-primary/10 text-primary dark:text-white border-r-2 border-primary'
                            : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                          'group flex gap-x-3 rounded-lg p-3 text-sm font-medium transition-all duration-200 hover-lift w-full justify-start'
                        )}
                        onClick={onClose}
                        variant="ghost"
                      >
                        <item.icon
                          className={clsx(
                            isActive ? 'text-primary dark:text-white' : 'text-muted-foreground group-hover:text-foreground',
                            'h-5 w-5 shrink-0'
                          )}
                          aria-hidden="true"
                        />
                        {item.name}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </li>
          )}

          {/* Agent Availability Toggle */}
          {isAgent && (
            <li>
              <div className="mx-2 p-3 rounded-xl bg-card border border-border shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-x-2">
                    <div className={clsx(
                      'h-2.5 w-2.5 rounded-full transition-colors duration-300',
                      presenceLost
                        ? 'bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.6)]'
                        : isAvailable
                          ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)]'
                          : 'bg-gray-400'
                    )} />
                    <span className="text-sm font-medium text-foreground">
                      {isAvailable ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isAvailable}
                    disabled={availabilityLoading}
                    onClick={handleAvailabilityToggle}
                    className={clsx(
                      'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                      isAvailable ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600',
                      availabilityLoading && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    <span
                      className={clsx(
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-300 ease-in-out',
                        isAvailable ? 'translate-x-5' : 'translate-x-0'
                      )}
                    />
                  </button>
                </div>
                {presenceLost ? (
                  <p className="mt-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                    Connection lost — the dialer has stopped sending you calls. Check your
                    internet; this clears on its own once you are back.
                  </p>
                ) : (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {isAvailable ? 'You will receive incoming calls' : 'You will not receive any calls'}
                  </p>
                )}
              </div>
            </li>
          )}

          {isAdmin && phoneHealth && (
            <li>
              <div className="mx-2 p-3 rounded-xl bg-gradient-to-br from-card to-muted/30 border border-border shadow-sm text-xs">
                <p className="font-semibold text-foreground mb-2.5">Number Health</p>
                <div className="space-y-1.5 text-muted-foreground">
                  <div className="flex justify-between"><span>Total</span><span className="font-semibold text-foreground tabular-nums">{phoneHealth.total}</span></div>
                  <div className="flex justify-between text-green-600"><span>Healthy</span><span className="tabular-nums">{phoneHealth.healthy}</span></div>
                  <div className="flex justify-between text-amber-600"><span>Warning</span><span className="tabular-nums">{phoneHealth.warning}</span></div>
                  <div className="flex justify-between text-red-600"><span>Replace</span><span className="tabular-nums">{phoneHealth.replace}</span></div>
                  {phoneHealth.warmUp > 0 && (
                    <div className="flex justify-between text-blue-600"><span>Warm-Up</span><span className="tabular-nums">{phoneHealth.warmUp}</span></div>
                  )}
                  {phoneHealth.carrierSpamIssues > 0 && (
                    <div className="flex justify-between text-orange-600 pt-1 border-t border-border/50"><span>Spam flags</span><span className="tabular-nums font-medium">{phoneHealth.carrierSpamIssues}</span></div>
                  )}
                </div>
              </div>
            </li>
          )}

          {/* User info */}
          <li className="mt-auto">
            <div className="mx-2 p-3 rounded-xl bg-card border border-border shadow-sm">
              <div className="flex items-center gap-x-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
                  <span className="text-sm font-semibold text-primary-foreground">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">
                    {user?.name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email}
                  </p>
                  <div className="mt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {user?.role}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  );

  return (
    <>
      {/* Mobile sidebar */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="-m-2.5 p-2.5 rounded-full bg-background/10 backdrop-blur-sm hover:bg-background/20 transition-colors duration-200"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-foreground" aria-hidden="true" />
                    </Button>
                  </div>
                </Transition.Child>
                <div className="scrollbar-thin flex grow flex-col gap-y-5 overflow-y-auto bg-background border-r border-border">
                  <SidebarContent />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <div className="scrollbar-thin flex grow flex-col gap-y-5 overflow-y-auto border-r border-border bg-background/95 backdrop-blur-sm">
          <SidebarContent />
        </div>
      </div>
    </>
  );
};

export default Sidebar;